/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {
  AfterContentInit,
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import {EntityTreeBranchModel} from '../models/entity-tree-branch.model';
import {EntityTreeModel} from '../models/entity-tree.model';
import {EntityElementModel} from '../entity-element/models/entity-element.model';
import {KeyValue} from '@angular/common';
import {EntityTreeUtilService} from '../services/entity-tree-util.service';
import {EntityTreeUpdateModel} from './entity-tree-update.model';
import {EntityElementActionsEnum} from '../entity-element/models/entity-element-actions.enum';
import {EntityTypeEnum} from '../../../settings/business-settings/configuration/v1/entity-type.enum';
import {TreeNodeModel} from '../../../settings/business-settings/models/tree-node-model';
import {ContextMenuActionEnum} from '../entity-element/models/context-menu-action.enum';
import {EntityTreeBranchTypeEnum} from '../models/entity-tree-branch-type.enum';
import {EntityTreeDisplayModeEnum} from '../../../settings/business-settings/models/entity-tree-display-mode.enum';
import {EntityElementPathStateModel} from '../entity-element/models/entity-element-path-state.model';
import {EntityPathStateService} from '../services/paths-state/entity-path-state.service';
import {EntityTreePathStateIdentificationService} from '../services/paths-state/entity-tree-path-state-identification.service';
import {EntityTreeBranchDisplayDataModel} from '../../../settings/business-settings/configuration/v1/models/entity-tree-branch-display-data.model';
import {BusinessSettingsDisplayDataModel} from '../../../settings/business-settings/configuration/v1/models/business-settings-display-data.model';
import {Subscription, timer} from 'rxjs';
import {EntityCodeTypeModel} from '../models/entity-code-type.model';
import {EntityBranchActionEnum} from '../models/entity-branch-action.enum';

interface EntityTreeStateModel {
    selectedSpecificBranch: Map<EntityTypeEnum, EntityTreeBranchModel>;
}

@Component({
    selector: 'app-entity-tree',
    templateUrl: './entity-tree.component.html',
    styleUrls: ['./entity-tree.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EntityTreeComponent implements AfterContentInit, AfterViewInit, OnChanges, OnInit, OnDestroy {
    @Input() displayMode: EntityTreeDisplayModeEnum;
    @Input() entityTree: EntityTreeModel = {} as EntityTreeModel;
    @Input() entityTreeData: BusinessSettingsDisplayDataModel;
    @Input() configurationTreeNode: TreeNodeModel = {} as TreeNodeModel;
    @Output() updatedTree = new EventEmitter<EntityTreeUpdateModel>();
    @Output() updatedPaths = new EventEmitter<TreeNodeModel>();

    @Input() setTreePath = new EventEmitter<EntityCodeTypeModel[]>();
    @Input() editEntityElement = new EventEmitter<EntityCodeTypeModel>();

    @ViewChild('specificBranchButton') specificBranchButtonTemplate: TemplateRef<any>;

    public openEditEntityModal = new EventEmitter<EntityCodeTypeModel>();

    public entitiesData: Map<EntityTypeEnum, EntityTreeBranchDisplayDataModel> =
            new Map<EntityTypeEnum, EntityTreeBranchDisplayDataModel>();
    public state: EntityTreeStateModel = {
        selectedSpecificBranch: null
    };
    // Subscriptions variable
    private subscriptions = new Subscription();

    constructor(public entityUtil: EntityTreeUtilService,
                private pathState: EntityTreePathStateIdentificationService,
                private entityPathState: EntityPathStateService) {
    }

    ngOnInit(): void {
        this.subscriptions.add(this.setTreePath.subscribe((path: EntityCodeTypeModel[]) => {
            this.setPath(path);
        }));

        this.subscriptions.add(this.editEntityElement.subscribe((entity: EntityCodeTypeModel) => {
            this.openEntityElementEditModalHandler(entity);
        }));
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    ngAfterContentInit(): void {
        if (this.entityTreeData != null) {
            this.entitiesData = this.entityTreeData?.entitiesData;
        }
        this.updatePathsState();
        this.refreshState();
    }

    ngAfterViewInit(): void {
        this.pathState.setTreeInitialTabsState(this.entityTree, this.configurationTreeNode, this.entityTreeData);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.configurationTreeNode != null && !changes.configurationTreeNode.firstChange) {
            this.pathState.setTreeInitialTabsState(this.entityTree, this.configurationTreeNode, this.entityTreeData);
            this.updatePathsState();
        }
    }

    /**
     * Emits the updated tree values
     */
    emitUpdatedTreeValue(): void {
        this.updatedTree.emit({tree: this.entityTree, relations: this.configurationTreeNode});
    }

    /**
     * Adds item to pool
     */
    addToPool(newElement: EntityElementModel, entity: EntityTreeBranchModel): void {
        if (newElement != null && entity != null && entity.pool != null) {
            // Update pool
            if (entity.updatePoolItemsAfterElementChangeFunction != null) {
                entity.pool = entity.updatePoolItemsAfterElementChangeFunction(newElement, entity.pool);
            }

            // Update the new element context menu actions with specific rules
            if (entity.updatePoolNewItemContextMenuActions != null && this.enableEntity(entity)) {
                newElement.contextMenuActions =
                        entity.updatePoolNewItemContextMenuActions(newElement, entity.pool, entity.connections);
            } else {
                // Generic new element context menu actions update
                const parent = this.getActiveParentEntity(entity);
                this.updatePoolItemContextMenu(newElement, entity, parent);
            }

            entity.pool.push(newElement);
            this.emitUpdatedTreeValue();
        }
    }

    /**
     * Reorders the pool items
     */
    changePoolItemsOrder(poolItems: EntityElementModel[], entity: EntityTreeBranchModel): void {
        entity.pool = poolItems;
        this.emitUpdatedTreeValue();
    }

    /**
     * Edit item from pool
     * @param newElement The new updated element
     * @param entity The entity to which the new element belongs
     */
    editPoolItem(newElement: EntityElementModel, entity: EntityTreeBranchModel): void {
        const entityData = this.entityUtil.getEntityDisplayData(entity.name, this.entitiesData);

        if (newElement != null) {
            const originalValue = newElement.originalValue;

            // Update relation tree
            if (newElement.code !== originalValue.code) {
                this.entityUtil.replaceRelationTreeNodesCodes(newElement.code, originalValue.code, this.configurationTreeNode);
            }

            // Update pool
            if (entity.updatePoolItemsAfterElementChangeFunction != null) {
                entity.pool = entity.updatePoolItemsAfterElementChangeFunction(newElement, entity.pool);
            }

            // Update newElement original value
            if (entity.mapOriginalValueFunction != null) {
                newElement.originalValue = entity.mapOriginalValueFunction(newElement);
            }

            if (entity.pool != null) {

                entity.pool = entity.pool.map((item) => item.originalValue.code === originalValue.code ?
                        {...newElement, contextMenuActions: [...newElement.contextMenuActions]} : item);

                if (this.entityUtil.hasNotNullConnections(entity, entityData)
                        && this.retrieveEntryFromArrayByCode(entity.connections, originalValue.code)) {
                    entity.connections[entity.connections.findIndex((item) => item.code === originalValue.code)] = {
                        ...newElement,
                        action: EntityElementActionsEnum.DELETE,
                        connectedWithParent: false,
                        isSelected: this.entityUtil.hasNotNullPath(entity, entityData) && entity.path.code === originalValue.code
                    };
                }

                if (this.entityUtil.hasNotNullPath(entity, entityData) && entity.path.code === originalValue.code) {
                    entity.path = {...newElement, connectedWithParent: false};
                }

                // Update all context menu actions in the pool with specific rules
                if (entity.updatePoolItemsContextMenuActions != null && this.enableEntity(entity)) {
                    entity.pool = entity.updatePoolItemsContextMenuActions(entity.pool, entity.connections);
                }
            }

            if (originalValue.active !== newElement.active) {
                this.updatePathsState(entity);
            }

            this.emitUpdatedTreeValue();
        }
    }

    /**
     * Adds pool item to the connections
     */
    addToConnections(element: EntityElementModel, entity: EntityTreeBranchModel, fromAddToPath: boolean = false): void {
        element.isANewEntityElement = false;
        const parent = this.getActiveParentEntity(entity);
        const parentData = this.entityUtil.getEntityDisplayData(parent?.name, this.entitiesData);
        const entityData = this.entityUtil.getEntityDisplayData(entity.name, this.entitiesData);
        if (entityData?.hasConnections) {
            const newElement = {
                ...element,
                connectedWithParent: true,
                action: EntityElementActionsEnum.DELETE
            };

            const poolItem = this.retrieveEntryFromArrayByCode(entity.pool, element.code);
            if (this.entityHasSingleConnection(entity) && entity.connections != null) {
                entity.connections = [newElement];
            } else if (entity.connections != null) {
                entity.connections.push(newElement);
            }

            if (parent != null && parentData != null) {
                if (parentData.hasPath) {
                    poolItem.connectedWithParent = true;
                    poolItem.displayParentCode = entity.hasMultipleConnectedParents ? null : parent.path.childrenCode;
                    poolItem.parentCode = entity.hasMultipleConnectedParents ? null : parent.path.code;
                    poolItem.childrenCode = entity.childrenCodeFunction != null ? entity.childrenCodeFunction(poolItem) : element.code;
                    newElement.parentCode = parent.path.code;
                    newElement.childrenCode = entity.childrenCodeFunction != null ? entity.childrenCodeFunction(poolItem) : element.code;
                    newElement.displayParentCode = entity.hasMultipleConnectedParents ? null : parent.path.childrenCode;
                }
            }

            // Relation tree
            this.entityUtil.addToConnectionsUpdateRelationTree(
                    newElement, entity, this.configurationTreeNode, this.entityTree, this.entitiesData);

            this.updateCurrentEntityPoolItemsContextMenus(entity);
            if (entity.updatePoolItemsAfterAddElementToConnections != null) {
                entity.pool = [...entity.updatePoolItemsAfterAddElementToConnections(element, entity.pool)];
            }

            // If it is the only connection -> add to path
            if (entityData.hasPath && entity.connections.length === 1 && (entity.path == null || (entity.path.code !== newElement.code))) {
                newElement.isSelected = true;
                this.addToPath(newElement, entity);
            }

            if (!fromAddToPath) {
                this.updatePathsState(entity);
            }
        }

        this.emitUpdatedTreeValue();
    }

    /**
     * Adds connection item to path
     */
    addToPath(newElement: EntityElementModel, entity: EntityTreeBranchModel): void {
        const entityData = this.entityUtil.getEntityDisplayData(entity.name, this.entitiesData);
        newElement.isANewEntityElement = false;
        if (this.hasSkippableChild(entity)) {
            this.updateEntityPaths(entity, newElement.code);
        }
        // Create a new element reference and set action as delete
        const element = {
            ...newElement,
            action: EntityElementActionsEnum.DELETE,
            connectedWithParent: false,
            isSelected: true
        };
        // Add new element to the branch path
        if (entityData?.hasPath) {
            // If the entity has connections and the element is not yet in the connections section -> add to connections
            if (this.entityUtil.hasNotNullConnections(entity, entityData)
                    && this.retrieveEntryFromArrayByCode(entity.connections, element.code) == null) {
                this.addToConnections({...element, isSelected: true, contextMenuActions: []}, entity, true);
            }
            entity.path = {
                ...element,
                contextMenuActions: [],
                childrenCode: element.childrenCode == null ? this.getActiveParentEntity(entity)?.path.childrenCode : newElement.childrenCode
            };
        }

        this.updateCurrentEntityPoolItemsContextMenus(entity);
        if (entity.pathTo != null) {
            entity.pathTo.forEach((child) => {
                const childBranch = this.entityUtil.getEntityBranch(child.type, this.entityTree);
                if (childBranch != null) {
                    this.updateEntityAttributesRecursively(childBranch.get(child.type), childBranch);
                }
            });
        }
        this.updatePathsState(entity);
    }

    /**
     * Removes a the path item
     */
    removeFromPath(element: EntityElementModel, entity: EntityTreeBranchModel): void {
        entity.path = null;
        if (this.entityUtil.hasSelectedConnections(entity)) {
            this.retrieveEntryFromArrayByCode(entity.connections, element.code).isSelected = false;
        }
        const poolItem = this.retrieveEntryFromArrayByCode(entity.pool, element.code);
        this.updatePoolItemContextMenu(poolItem, entity, this.getActiveParentEntity(entity));

        if (entity.pathTo != null) {
            entity.pathTo.forEach((child) => {
                const entityBranch = this.entityUtil.getEntityBranch(child.type, this.entityTree);
                if (entityBranch != null) {
                    this.resetEntityAttributesRecursively(entityBranch.get(child.type));
                }
            });
        }
    }

    /**
     * Removes a connection item
     */
    removeFromConnections(element: EntityElementModel, entity: EntityTreeBranchModel): void {
        this.removeFromConnectionsVisualContext(element, entity);
        // Update relation tree
        this.entityUtil.removeFromConnectionsUpdateRelationTree(
                element, entity, this.configurationTreeNode, this.entityTree, this.entitiesData);
        if (entity.updatePoolItemsAfterRemoveElementFromConnections != null) {
            entity.pool = [...entity.updatePoolItemsAfterRemoveElementFromConnections(entity.pool, entity.connections)];
        }

        this.updatePathsState(entity);

        this.emitUpdatedTreeValue();
    }

    /**
     * Removes a entity from pool.
     */
    removeFromPool(element: EntityElementModel, entity: EntityTreeBranchModel): void {
        if (element != null && entity != null) {
            // remove visual elements
            this.removeFromConnections(element, entity);

            // remove from all tree node paths
            this.entityUtil.removeAllElementCodeFromRelationTree(element.code, this.configurationTreeNode.relationTree);

            // remove from pool
            entity.pool.splice(entity.pool.findIndex((item) => item.code === element.code), 1);

            // remove excluded decision codes
            if (entity.updatePoolItemsAfterElementDeleteFunction != null) {
                entity.updatePoolItemsAfterElementDeleteFunction(element, entity.pool);
            }

            this.updatePathsState(entity);

            this.emitUpdatedTreeValue();
        }
    }

    /**
     * Remaps the pathTo and pathFrom values for a given entity (and its fathers, children and brothers) to represent
     * the correct visual display
     * @param entity the {@link EntityTreeBranchModel} to remap
     * @param skip if the entity is to be skipped {@link boolean}
     */
    skipEntityToggle(entity: EntityTreeBranchModel, skip: boolean): void {
        const entityDisplayData = this.entityUtil.getEntityDisplayData(entity.name, this.entitiesData);

        const currentTreeNode = this.entityUtil.getCurrentEntityTreeNode(
                entity,
                this.configurationTreeNode,
                this.entityTree,
                this.entitiesData
        );

        if (skip) {
            currentTreeNode.activeChildren = this.entitiesData.get(currentTreeNode.type).pathTo
                    .map((item) => item.type)
                    .filter((child) => child !== entity.name);
        } else {
            // Add the entity to the active children
            currentTreeNode.activeChildren.push(entity.name);

            // Remove excluded entities from the active children
            const parentDisplayData = this.entitiesData.get(currentTreeNode.type);
            const excludes = parentDisplayData.pathTo.find((child) => child.type === entity.name).excludes;
            currentTreeNode.activeChildren = currentTreeNode.activeChildren.filter((child) => child !== excludes);
        }

        if (entityDisplayData != null) {
            this.skipEntityToggleRemapEntityPathFrom(entityDisplayData, entity.name, currentTreeNode);
            this.skipEntityToggleRemapEntityPathTo(entityDisplayData, entity.name, currentTreeNode);

            this.updateCurrentEntityPoolItemsContextMenus(entity);
            this.resetRelationTreeFromEntity(entity);

            this.updatePathsState(entity);
            this.emitUpdatedTreeValue();
        }
    }

    /**
     * Keyvalue pipe comparer function to maintain to sort the maps to the entry order
     *  The keyvalue pipe sorts the items by key order by default. We must provide a comparer function to change that order,
     *  and sort the items according to the position attribute.
     */
    orderByPosition = (a: KeyValue<EntityTypeEnum, number>, b: KeyValue<EntityTypeEnum, number>): number =>
            b.value > a.value ? -1 : (a.value > b.value ? 1 : 0);

    /**
     * Manages the click in a specific tab on the specific branch tabs
     * @param tab The clicked tab ({@link EntityTreeBranchOptionModel}) option
     */
    clickSpecificBranchTab(tab: EntityTreeBranchTypeEnum): void {
        const tabDisplayData = this.entityTreeData?.branchesData.get(tab);
        if (tabDisplayData.selectTabAction != null) {
            tabDisplayData.selectTabAction(this.entityTreeData, this.entityTree.commonBranch, this.updateEntityAttributesRecursively);
            this.updatePathsState();
        }

        this.entityTree.specificBranchOptions.forEach((item) => {
            item.isSelected = item.value === tab;
        });
        this.refreshState();
    }

    /**
     * Returns the correct path connector class
     * (connects the path nodes between themselves --> line inside the entity row)
     * @param entity The {@link EntityTreeBranchModel} in which the path connect has to be drawn
     * @return the path connector class
     */
    pathConnectorLengthClass(entity: EntityTreeBranchModel): string {
        let lengthClass = null;

        if (entity != null) {

            const entityDisplayData = this.entityUtil.getEntityDisplayData(entity.name, this.entitiesData);

            if (entityDisplayData?.pathFrom != null) {
                // Set values if there is an active parent
                const activePathFrom = this.entityUtil.retrieveEntryFromArrayByActive(entityDisplayData.pathFrom, true);
                let activeParentDisplayData = null;
                let activeParentEntity = null;
                if (activePathFrom != null) {
                    activeParentDisplayData = this.entityUtil.getEntityDisplayData(activePathFrom.type, this.entitiesData);
                    activeParentEntity = this.entityUtil.getEntityConfiguration(activePathFrom.type, this.entityTree);

                } else {
                    // Set values if no parent is active
                    const inactivePathFrom = this.entityUtil.retrieveEntryFromArrayByActive(entityDisplayData.pathFrom, false);
                    let inactiveParentEntity = null;
                    let inactiveParentEntityData = null;
                    if (inactivePathFrom != null) {
                        inactiveParentEntity = this.entityUtil.getEntityConfiguration(inactivePathFrom.type, this.entityTree);
                        inactiveParentEntityData = this.entityUtil.getEntityDisplayData(inactivePathFrom.type, this.entitiesData);
                    }
                    if (inactiveParentEntity != null && inactiveParentEntityData.hasPath
                            && inactiveParentEntity.path != null) {
                        if (inactiveParentEntity.pathTo.length > 1) {
                            lengthClass = 'full';
                        } else if (entity.pathTo == null && !entityDisplayData.hasPath && entityDisplayData.hasConnections) {
                            lengthClass = 'short';
                        }
                    }
                }

                if (activeParentEntity != null && activeParentDisplayData?.hasPath) {

                    if (entityDisplayData.pathTo != null && entityDisplayData.pathTo.length > 0) {
                        if (entityDisplayData.hasPath) {
                            if (entity.path != null) {
                                lengthClass = 'full';
                            } else {
                                lengthClass = 'final';
                            }
                        }
                    } else {
                        if (entityDisplayData.hasPath) {
                            lengthClass = 'final';
                        } else if (entityDisplayData.hasConnections) {
                            lengthClass = 'short';
                        }
                    }
                    if (activeParentDisplayData.pathTo.length > 1) {
                        if ((this.entityUtil.hasNotNullPath(entity, entityDisplayData))
                                || (!entityDisplayData.hasPath && entityDisplayData.hasConnections)) {
                            lengthClass = 'full';

                            if (activeParentDisplayData.pathTo.findIndex((item) => !item.active) > -1
                                    && (entityDisplayData.pathTo != null && entityDisplayData.pathTo.length === 0
                                            || entityDisplayData.pathTo == null)) {
                                lengthClass = 'short';
                            }

                        } else {
                            lengthClass = 'final';
                        }
                    }
                }
            } else {
                if (entityDisplayData?.pathTo != null && entityDisplayData.pathTo.length > 0 && entityDisplayData.hasPath) {
                    lengthClass = 'initial';
                }
            }
            return lengthClass;
        }
    }

    /**
     * Returns the correct connections connector class
     * (connects the path nodes connector with the connections connector)
     * @param entity The {@link EntityTreeBranchModel} in which the connections connector has to be drawn
     * @return the connections connector class
     */
    pathToConnectionsConnectorClass(entity: EntityTreeBranchModel): string {
        const parent = this.getActiveParentEntity(entity);
        const entityData = this.entityUtil.getEntityDisplayData(entity.name, this.entitiesData);

        if (this.enableEntity(entity)) {
            let connectorClass: string;
            if (parent != null) {
                if (this.entityUtil.hasSelectedPathNodeOrConnections(parent)) {

                    if (entityData.hasConnections) {
                        connectorClass = 'node-connections-connector';
                    }
                    if (this.hasClosingConnectionsConnector(entity)) {
                        // TODO: CTWILS-NA : After refactor the polls, paths and connections areas, this line should be updated
                        // connectorClass += ' ' + 'closing-connector';
                        connectorClass += '';
                    }
                }
            } else {
                connectorClass = 'node-connections-connector first-entity';
            }
            return connectorClass;
        } else {
            if (entity.skippable && this.hasEntityConnector(entity)) {
                return 'node-connections-connector skippable';
            }
        }
    }

    /**
     * Checks if an entity has a path connector or not
     * @param entity The {@link EntityTreeBranchModel} to check
     * @return boolean If the entity should have a path connector or not
     */
    hasPathNodeConnector(entity: EntityTreeBranchModel): boolean {
        let hasConnector = this.enableEntity(entity) || entity.skippable;

        if (hasConnector) {
            const parent = this.getActiveOrInactiveParent(entity);
            const parentData = this.getEntityDisplayData(parent);

            if (parent != null && parentData != null) {
                if (parentData.hasPath) {
                    hasConnector = this.entityUtil.hasSelectedPathNode(parent);
                } else if (parentData.hasConnections) {
                    hasConnector = this.entityUtil.hasSelectedConnections(parent);
                }
            } else {
                hasConnector = this.entityUtil.hasSelectedPathNodeOrConnections(entity);
            }
        }

        return hasConnector;
    }

    /**
     * Checks if and entity should have a connector to the previous entity
     * (connector between entities --> line outside the entity row)
     * @param entity The entity {@link EntityTreeBranchModel}
     * @return boolean If the entity should have a connector to the previous entity or not
     */
    hasEntityConnector(entity: EntityTreeBranchModel): boolean {
        const parent = this.getActiveOrInactiveParent(entity);
        const parentData = this.entityUtil.getEntityDisplayData(parent?.name, this.entitiesData);

        if (parentData != null) {
            if (parentData.hasPath) {
                return this.entityUtil.hasSelectedPathNode(parent);
            } else if (parentData.hasConnections) {
                return this.entityUtil.hasSelectedConnections(parent);
            }
        }
        return false;
    }

    /**
     * Returns if the specific branch tab component should have the add/cut display and connectors
     */
    hasAddAndCutSpecificBranch(): boolean {
        let hasCutAndAdd = false;
        if (this.entityTree != null && this.entityTree.commonBranch != null) {
            const lastCommonBranchEntity = this.entityUtil.lastValueInBranch(this.entityTree.commonBranch,
                    this.entityTreeData?.entitiesOrder.commonBranch);
            const lastCommonBranchEntityData = this.getEntityDisplayData(lastCommonBranchEntity);
            hasCutAndAdd = this.entityUtil.entityIsInitialized(lastCommonBranchEntity, lastCommonBranchEntityData);
        }
        return hasCutAndAdd;
    }

    /**
     * Determines if an entity should have a closing connections connector
     * This connector should appear when the entity:
     * 1. Is the last one having connections or a path
     * 2. Has selected connections
     * 3. Does not have a path node
     * @param entity The {@link EntityTreeBranchModel} to check
     * @return boolean
     */
    hasClosingConnectionsConnector(entity: EntityTreeBranchModel): boolean {
        const parent = this.getActiveParentEntity(entity);
        return entity.pathTo == null && (parent != null && parent.pathTo.length === 1);
    }

    /**
     * Returns if an entity should only have a single connections
     * @param entity the {@link EntityTreeBranchModel} to check
     */
    entityHasSingleConnection(entity: EntityTreeBranchModel): boolean {
        return this.entityUtil.entityHasSingleConnection(entity, this.entityTree, this.entitiesData);
    }

    /**
     * Given an entity, returns its tree node path state
     */
    getTreeNodePathState(entity: EntityTreeBranchModel): EntityElementPathStateModel {
        return this.entityPathState.getTreeNodePathState(entity, this.configurationTreeNode, this.entityTree, this.entityTreeData,
                this.getSelectedSpecificBranchType());
    }

    getEntityDisplayData(entity: EntityTreeBranchModel): EntityTreeBranchDisplayDataModel {
        return this.entitiesData.get(entity?.name);
    }

    getEntityConfiguration(entity: EntityTypeEnum): EntityTreeBranchModel {
        return this.entityUtil.getEntityConfiguration(entity, this.entityTree);
    }

    enableEntity(entity: EntityTreeBranchModel): boolean {
        let enable = this.entityUtil.enableEntity(entity, this.entitiesData, this.entityTree);

        const isSpecificBranch = this.entityTree.commonBranch.get(entity.name) == null;

        if (isSpecificBranch) {
            const entitySpecificBranch = this.entityTree.specificBranchOptions.find((option) =>
                    this.entityTree.specificBranches.get(option.value)
                            .has(entity.name));

            const isFirstEntityInSpecificBranch = this.entityUtil.isFirstItemInBranch(
                    this.entityTreeData?.entitiesOrder.specificBranches.get(entitySpecificBranch.value), entity.name);

            if (isFirstEntityInSpecificBranch) {
                const currentTreeNode = this.entityUtil.getCurrentEntityTreeNode(
                        entity, this.configurationTreeNode, this.entityTree, this.entitiesData);

                let selectedBranchIsActive = false;

                if (currentTreeNode != null && currentTreeNode.pathState != null && currentTreeNode.pathState.tabsState != null) {
                    selectedBranchIsActive = currentTreeNode.pathState.tabsState.get(entitySpecificBranch.value).isActive;
                }
                enable = enable && selectedBranchIsActive;
            }
        }
        return enable;
    }

    /**
     * Cuts a given specific branch from the entity-tree
     * @param branch the specific branch {@link EntityTreeBranchTypeEnum} to cut
     */
    cutBranch(branch: EntityTreeBranchTypeEnum): void {
        const specificBranchRootEntity = this.entityUtil.firstValueInBranch(
                this.entityTree.specificBranches.get(branch), this.entityTreeData?.entitiesOrder.specificBranches.get(branch));
        const commonBranchLastEntity = this.entityUtil.lastValueInBranch(this.entityTree.commonBranch,
                this.entityTreeData?.entitiesOrder.commonBranch);

        this.resetSpecificBranchRelationTree(specificBranchRootEntity.name, EntityBranchActionEnum.CUT);

        this.toggleSpecificBranchTabState(branch, commonBranchLastEntity, false);

        this.resetEntityAttributesRecursively(specificBranchRootEntity);
        this.updatePathsState(commonBranchLastEntity);

        this.emitUpdatedTreeValue();
    }

    /**
     * Adds a given specific branch to the entity-tree
     * @param branch the specific branch {@link EntityTreeBranchTypeEnum} to add
     */
    addBranch(branch: EntityTreeBranchTypeEnum): void {
        const commonBranchLastEntity = this.entityUtil.lastValueInBranch(this.entityTree.commonBranch,
                this.entityTreeData?.entitiesOrder.commonBranch);
        const specificBranchRootEntity = this.entityUtil.firstValueInBranch(
                this.entityTree.specificBranches.get(branch), this.entityTreeData?.entitiesOrder.specificBranches.get(branch));

        this.resetSpecificBranchRelationTree(specificBranchRootEntity.name, EntityBranchActionEnum.ADD);

        this.updatePathsState(commonBranchLastEntity);

        this.toggleSpecificBranchTabState(branch, commonBranchLastEntity, true);

        this.updateCurrentEntityPoolItemsContextMenus(specificBranchRootEntity);

        this.emitUpdatedTreeValue();
    }

    /**
     * Toggles the state of a given specific branch tab
     */
    toggleSpecificBranchTabState(branch: EntityTreeBranchTypeEnum, tabParentEntity: EntityTreeBranchModel, active: boolean): void {
        const currentTreeNode = this.entityUtil.getCurrentEntityTreeNode(
                tabParentEntity, this.configurationTreeNode, this.entityTree, this.entityTreeData?.entitiesData);

        this.pathState.toggleSpecificBranchTabState(branch, tabParentEntity, currentTreeNode, this.entityTree, active);
        this.updatePathsState(tabParentEntity);
        this.updateConnectionsPathStateRecursively(currentTreeNode, tabParentEntity.name);
    }

    getSelectedBranchOrderMap(): Map<EntityTypeEnum, number> {
        return this.entityTreeData?.entitiesOrder.specificBranches.get(this.getSelectedSpecificBranchType());
    }

    treeNodeIsInOriginalPosition(entity: EntityTreeBranchModel): boolean {
        let currentPosition = null;
        // If entity is in common branch
        if (this.entityTree.commonBranch.get(entity.name) != null) {
            currentPosition = this.entityTreeData?.entitiesOrder.commonBranch.get(entity.name);
        } else {
            this.entityTree.specificBranches.forEach((value, key) => {
                if (value.get(entity.name) != null) {
                    currentPosition = this.entityTreeData?.entitiesOrder.specificBranches.get(key)
                            .get(entity.name);
                }
            });
        }
        const configuration = this.entityUtil.getEntityConfiguration(entity.name, this.entityTree);

        return currentPosition === configuration.originalPosition;
    }

    /**
     * Remaps an entity and its relations pathTo properties after a skip toggle.
     * @param entityDisplayData the entity display data
     * @param entity that entity that was subject to the skip toggle
     * @param currentTreeNode the {@link TreeNodeModel} in which the entity is currently held
     */
    private skipEntityToggleRemapEntityPathTo(entityDisplayData: EntityTreeBranchDisplayDataModel,
                                              entity: EntityTypeEnum,
                                              currentTreeNode: TreeNodeModel): void {
        if (entityDisplayData.pathTo != null) {

            entityDisplayData.pathTo.forEach((child) => {
                // Entity path to
                child.active = currentTreeNode.activeChildren.includes(entity);

                // Children pathFrom
                const childEntityDisplayData = this.entityUtil.getEntityDisplayData(child.type, this.entitiesData);
                if (childEntityDisplayData != null && childEntityDisplayData.pathFrom != null) {
                    this.retrieveEntryFromArrayByType(childEntityDisplayData.pathFrom, entity)
                            .active = currentTreeNode.activeChildren.includes(entity);
                }
            });
        }
    }

    /**
     * Remaps an entity and its relations pathFrom properties after a skip toggle.
     * @param entityDisplayData the entity display data
     * @param entity that entity that was subject to the skip toggle
     * @param currentTreeNode the {@link TreeNodeModel} in which the entity is currently held
     */
    private skipEntityToggleRemapEntityPathFrom(entityDisplayData: EntityTreeBranchDisplayDataModel,
                                                entity: EntityTypeEnum,
                                                currentTreeNode: TreeNodeModel): void {
        if (entityDisplayData.pathFrom != null) {
            entityDisplayData.pathFrom.forEach((parent) => {
                // Entity pathFrom
                parent.active = currentTreeNode.activeChildren.includes(entity);

                // Parents pathTo (pathTo with entity as child)
                const parentPathTo = this.entityUtil.getEntityDisplayData(parent.type, this.entitiesData).pathTo;

                if (parentPathTo != null) {
                    parentPathTo.forEach((child) => {

                        if (child.type === entity) {
                            child.active = currentTreeNode.activeChildren.includes(entity);
                        }

                        // Sister/Child entities
                        if (entityDisplayData.pathTo != null) {
                            // An entity present in both entity.pathTo and parent.pathTo
                            const sisterEntity = this.retrieveEntryFromArrayByType(entityDisplayData.pathTo, child.type);
                            if (sisterEntity != null) {
                                // Must have its parent.pathTo.active value set equal to entity.pathTo.active
                                child.active = currentTreeNode.activeChildren.includes(sisterEntity.type);
                                const sisterEntityDisplayData = this.entityUtil.getEntityDisplayData(
                                        sisterEntity.type, this.entitiesData);
                                if (sisterEntityDisplayData != null && sisterEntityDisplayData.pathFrom != null) {
                                    // For the pathFrom.type = entity.name, must set the active value to !skip
                                    // - If entity is to be skipped -> skip = true -> active = false
                                    // - If entity is not to be skipped -> skip = false -> active = true
                                    this.retrieveEntryFromArrayByType(sisterEntityDisplayData.pathFrom, parent.type)
                                            .active = currentTreeNode.activeChildren.includes(sisterEntity.type);
                                }
                            }
                        }
                    });
                }
            });
        }
    }

    private openEntityElementEditModalHandler(entity: EntityCodeTypeModel): void {
        if (this.entityTree.commonBranch.get(entity.type) == null) {
            this.changeSelectedTabToEntityBranch(entity.type);
        }
        // The modal must always be very last action
        timer(0)
                .subscribe(() => this.openEditEntityModal.emit(entity));
    }

    private changeSelectedTabToEntityBranch(entity: EntityTypeEnum): void {
        const specificBranch = this.entityTree.specificBranchOptions.find(
                (option) => this.entityTree.specificBranches.get(option.value)
                        .get(entity) != null);
        if (specificBranch != null) {
            this.clickSpecificBranchTab(specificBranch.value);
        }
    }

    private setPath(path: EntityCodeTypeModel[]): void {
        if (path?.length > 0) {
            const lastItem = path[path.length - 1];
            if (this.entityTree.commonBranch.get(lastItem.type) == null) {
                this.changeSelectedTabToEntityBranch(lastItem.type);
            }

            path.forEach((entry) => {
                const entity: EntityTreeBranchModel = this.entityUtil.getEntityConfiguration(entry.type, this.entityTree);
                const element: EntityElementModel = entity.pool.find((item) => item.code === entry.code);

                this.addToPath(element, entity);
            });
        }
    }

    /**
     * Resets the relation tree for a specific branch, given its root entity
     * @param specificBranchRootEntity the root entity of the specific branch to reset
     * @param action the action that triggers the reset
     */
    private resetSpecificBranchRelationTree(specificBranchRootEntity: EntityTypeEnum, action: EntityBranchActionEnum): void {
        const parentEntity = this.entityUtil.getActiveParentEntity(
                this.entityUtil.getEntityConfiguration(specificBranchRootEntity, this.entityTree),
                this.entityTree, this.entitiesData);
        const parentEntityPathCode = parentEntity.path?.code;

        const currentTreeNode = this.entityUtil.getCurrentEntityTreeNode(
                parentEntity, this.configurationTreeNode, this.entityTree, this.entitiesData);

        const currentLastCommonBranchEntityNode = currentTreeNode.relationTree.find((item) => item.code === parentEntityPathCode);

        if (action === EntityBranchActionEnum.CUT) {
            currentLastCommonBranchEntityNode.relationTree =
                    currentLastCommonBranchEntityNode.relationTree.filter((entry) => entry.type !== specificBranchRootEntity);

            currentLastCommonBranchEntityNode.activeChildren = currentLastCommonBranchEntityNode.activeChildren
                    .filter((child) => child !== specificBranchRootEntity);
        } else {
            currentLastCommonBranchEntityNode?.activeChildren.push(specificBranchRootEntity);
        }
    }

    private removeFromConnectionsVisualContext(element: EntityElementModel, entity: EntityTreeBranchModel): void {
        const entityData = this.entityUtil.getEntityDisplayData(entity.name, this.entitiesData);

        // If the item in the connections is also in the path, first remove it from path
        // this.entityUtil.hasNotNullPath is not used in this specific situation because the method returns the entity
        // current configuration value. The path must be cleared if the original or the current configurations have hasPath = true.
        if ((entityData.hasPath || entity.hasPath) && entity.path != null && entity.path.code === element.code) {
            this.removeFromPath(entity.path, entity);
        }
        // Remove from connections
        if (this.retrieveEntryFromArrayByCode(entity.connections, element.code) != null) {
            entity.connections.splice(entity.connections.findIndex((item) => item.code === element.code), 1);
        }
        // Update branch
        const poolItem = this.retrieveEntryFromArrayByCode(entity.pool, element.code);
        this.entityUtil.removeFromConnectionsUpdatePoolItem(poolItem);

        // Update context menus
        if (this.entityHasSingleConnection(entity)) {
            this.updateCurrentEntityPoolItemsContextMenus(entity);
        } else {
            this.updatePoolItemContextMenu(poolItem, entity, this.getActiveParentEntity(entity));
        }
        this.removeFromConnectionsUpdateChildrenPoolItemsRecursively(element, entity);
    }

    /**
     * Updates all the connections visual state for a given node
     */
    private updateConnectionsPathStateRecursively(node: TreeNodeModel, entity: EntityTypeEnum): void {
        let treeNode = node;

        if (treeNode == null && entity != null) {
            const entityConfiguration = this.entityUtil.getEntityConfiguration(entity, this.entityTree);
            if (entityConfiguration.isRoot && entityConfiguration.path != null) {
                treeNode = this.configurationTreeNode.relationTree.find((tree) => tree.code === entityConfiguration.path.code);
            }
        }

        if (treeNode != null) {
            const currentNodeEntity = this.entityUtil.getEntityBranch(treeNode.type, this.entityTree)
                    .get(treeNode.type);

            if (currentNodeEntity.connections != null) {
                currentNodeEntity.connections.forEach((item) => {
                    item.pathState = this.entityPathState.getElementPathState(currentNodeEntity, item,
                            this.configurationTreeNode, this.entityTree, this.entitiesData);
                });
            }
            if (currentNodeEntity.path != null) {
                currentNodeEntity.path.pathState = this.entityPathState.getElementPathState(currentNodeEntity, currentNodeEntity.path,
                        this.configurationTreeNode, this.entityTree, this.entitiesData);
            }

            if (treeNode.relationTree != null) {

                treeNode.relationTree.forEach((entry) => {
                    this.updateConnectionsPathStateRecursively(entry, entry.type);
                });
            }
        }
    }

    private updatePathsState(entity?: EntityTreeBranchModel): void {
        let currentTreeNode = null;
        if (entity != null) {
            const entityData = this.entityUtil.getEntityDisplayData(entity.name, this.entitiesData);
            currentTreeNode = this.entityUtil.getCurrentEntityTreeNode(
                    entity, this.configurationTreeNode, this.entityTree, this.entitiesData);

            if (currentTreeNode == null && entity.isRoot && entityData.hasPath && entity.path != null) {
                // If node is null, entity is root and its a new root element the configuration tree node as to be initialized
                if (this.configurationTreeNode.relationTree.find((entry) => entry.code === entity.path.code) == null) {
                    this.configurationTreeNode.relationTree.push({
                        code: entity.path.code,
                        relationTree: [],
                        type: entity.name,
                        activeChildren: entity.pathTo != null ? entity.pathTo.filter((item) => item.active)
                                .map((item) => item.type) : []
                    });
                }
            }
            this.pathState.setTreeNodeTabsState(currentTreeNode, entity, this.entityTree, this.entityTreeData);
        }

        this.pathState.identifyInactivePaths(this.configurationTreeNode, this.entityTree);
        this.pathState.identifyUnfinishedPaths(this.configurationTreeNode, this.entityTree, this.entityTreeData);

        const entityType = entity != null ? entity.name : null;
        this.updateConnectionsPathStateRecursively(currentTreeNode, entityType);

        this.updatedPaths.emit(this.configurationTreeNode);
    }

    private updatePoolItemContextMenu(item: EntityElementModel,
                                      entity: EntityTreeBranchModel,
                                      parent: EntityTreeBranchModel): void {
        const entityData = this.getEntityDisplayData(entity);
        item.contextMenuActions = [];
        // Add EDIT to all items
        this.entityUtil.addMenuAction(item.contextMenuActions, ContextMenuActionEnum.EDIT);
        this.entityUtil.addMenuAction(item.contextMenuActions, ContextMenuActionEnum.DELETE);

        // If the entity is enabled, add other actions
        if (this.enableEntity(entity)) {
            // If the entity has the path populated
            if (this.entityUtil.hasNotNullPath(entity, entityData)) {
                // If (entity has multiple connections AND pool item is not in path)
                // AND (pool item can be added to path with parent OR is the first entity from the common branch) -> add PUT IN PATH
                if ((!this.entityHasSingleConnection(entity) && !this.entityUtil.poolItemIsInPath(item.code, entity.path.code))
                        && (this.entityUtil.entityPoolItemCanBeAddToPathWithParent(entity, item, parent)
                                || this.entityUtil.isFirstItemInBranch(this.entityTreeData?.entitiesOrder.commonBranch, entity.name))) {

                    this.entityUtil.addMenuAction(item.contextMenuActions, ContextMenuActionEnum.PUT_IN_PATH);
                }
                // If the entity does not have the path populated, has path and can be added to path with parent -> add PUT IN PATH
            } else if (entityData.hasPath && this.entityUtil.entityPoolItemCanBeAddToPathWithParent(entity, item, parent)) {
                this.entityUtil.addMenuAction(item.contextMenuActions, ContextMenuActionEnum.PUT_IN_PATH);
            }

            // If the entity has selected connections
            if (this.entityUtil.hasSelectedConnections(entity)) {
                // If entity has multiple connections AND the item isn't already a connection
                // AND the item can be connected with parent -> add CONNECT
                if (!this.entityHasSingleConnection(entity)
                        && this.retrieveEntryFromArrayByCode(entity.connections, item.code) == null
                        && this.entityUtil.entityPoolItemCanBeConnectedWithParent(entity, item)) {
                    this.entityUtil.addMenuAction(item.contextMenuActions, ContextMenuActionEnum.CONNECT);
                }

                // If the entity does not have selected connections, has connections AND can be connected with parent -> add CONNECT
            } else if (entityData.hasConnections && this.entityUtil.entityPoolItemCanBeConnectedWithParent(entity, item)) {
                this.entityUtil.addMenuAction(item.contextMenuActions, ContextMenuActionEnum.CONNECT);
            }
        }
        // Change variable reference to force template update
        item.contextMenuActions = [...item.contextMenuActions];
    }

    /**
     * Retrieves an entry from an array by code
     * @param array the array from which to retrieve the entry
     * @param code the code of the entry to retrieve
     */
    private retrieveEntryFromArrayByCode<T = any>(array: T[], code: string): T | null {
        return array.find((item) => item['code'] === code);
    }

    /**
     * Retrieves an entry from an array by type
     * @param array the array from which to retrieve the entry
     * @param type the type of the entry to retrieve
     */
    private retrieveEntryFromArrayByType<T = any>(array: T[], type: string): T | null {
        return array.find((item) => item['type'] === type);
    }

    /**
     * For a given entity, returns if it has a skippable child
     * @param entity the {@link EntityTreeBranchModel} of the entity to check
     */
    private hasSkippableChild(entity: EntityTreeBranchModel): boolean {
        let skippableChild = false;
        if (entity.pathTo != null) {
            if (entity.pathTo.find((child) => this.entityUtil
                    .getEntityConfiguration(child.type, this.entityTree).skippable === true) != null) {
                skippableChild = true;
            }
        }
        return skippableChild;
    }

    /**
     * Resets the relation tree starting in a given entity
     * @param entity the {@link EntityTreeBranchModel} from each the tree is to be reset
     */
    private resetRelationTreeFromEntity(entity: EntityTreeBranchModel): void {
        // Visual update
        this.resetEntityAttributesRecursively(entity);
        // Relation tree update
        const node = this.entityUtil.getCurrentEntityTreeNode(
                entity, this.configurationTreeNode, this.entityTree, this.entitiesData);
        if (node != null) {
            node.relationTree = [];
        }
        this.emitUpdatedTreeValue();
    }

    /**
     * <p>For a given entity updates the {@link entityTreeData} pathTo and pathFrom values, as well as its children and
     * parents values, to match the {@link configurationTreeNode} path</p>
     * @param entity the {@link EntityTreeBranchModel} of the entity to update
     * @param nodeCode the code of the node to search on the {@link configurationTreeNode}
     */
    private updateEntityPaths(entity: EntityTreeBranchModel, nodeCode: string): void {
        // Retrieve the tree node were the element code is located (returns its father and brothers)
        const node = this.entityUtil.getCurrentEntityTreeNode(
                entity, this.configurationTreeNode, this.entityTree, this.entitiesData);

        if (node != null && node.relationTree != null) {
            // Retrieve the element code relation tree
            const newElementRelationTree = this.retrieveEntryFromArrayByCode(node.relationTree, nodeCode);

            this.entityUtil.getEntityDisplayData(entity.name, this.entitiesData).pathTo.forEach((item) => {
                // Remap the entity pathTo setting the active value for each one
                item.active = newElementRelationTree?.activeChildren?.includes(item.type);

                // Remap the child entities pathFrom setting the active value for each one
                const childEntityData = this.entityUtil.getEntityDisplayData(item.type, this.entitiesData);
                if (item.active) {
                    childEntityData.pathFrom.forEach((parent) => {
                        parent.active = entity.name === parent.type;
                    });
                } else {
                    childEntityData.pathFrom.forEach((parent) => {
                        parent.active = entity.name !== parent.type;
                    });

                }
                if (childEntityData.pathTo != null) {
                    childEntityData.pathTo.forEach((pathToEntry) => {
                        const grandChildEntityData = this.entityUtil
                                .getEntityDisplayData(pathToEntry.type, this.entitiesData);
                        if (grandChildEntityData.pathFrom != null) {
                            grandChildEntityData.pathFrom.forEach((pathFromEntry) => {
                                pathFromEntry.active = pathFromEntry.type === item.type;
                            });
                        }
                    });
                }
            });
        }
    }

    private refreshState(): void {
        this.state = {
            ...this.state,
            selectedSpecificBranch: this.getSelectedSpecificBranch()
        };
    }

    /**
     * Returns the active parent entity an entity
     * @param entity The entity {@link EntityTreeBranchModel}
     * @return EntityTreeBranchModel the active parent entity
     */
    private getActiveParentEntity(entity: EntityTreeBranchModel): EntityTreeBranchModel {
        return this.entityUtil.getActiveParentEntity(entity, this.entityTree, this.entitiesData);
    }

    /**
     * Returns the active or inactive parent entity an entity.
     * If there's an active parent, returns the active parent. Otherwise, returns the 1st inactive parent.
     * @param entity The entity {@link EntityTreeBranchModel}
     * @return EntityTreeBranchModel the parent entity
     */
    private getActiveOrInactiveParent(entity: EntityTreeBranchModel): EntityTreeBranchModel {
        return this.entityUtil.getActiveOrInactiveParent(entity, this.entityTree, this.entitiesData);
    }

    /**
     * Update all the children pool items, and its children, after an item is removed from connections
     */
    private removeFromConnectionsUpdateChildrenPoolItemsRecursively(element: EntityElementModel, entity: EntityTreeBranchModel): void {
        if (entity.pathTo != null) {
            entity.pathTo?.forEach((child) => {
                const branch = this.entityUtil.getEntityBranch(child.type, this.entityTree);
                if (branch != null) {
                    const childEntity = branch.get(child.type);
                    if (childEntity != null) {
                        childEntity.pool?.forEach((item) => {
                            if (item.parentCode === element.code) {
                                this.entityUtil.removeFromConnectionsUpdatePoolItem(item);
                                if (childEntity.pathTo != null) {
                                    this.removeFromConnectionsUpdateChildrenPoolItemsRecursively(item, childEntity);
                                }
                            }
                        });
                    }
                }
            });
        }
    }

    /**
     * Remaps the entity pool items context menu actions array
     * @param entity the related entity.
     */
    private updateCurrentEntityPoolItemsContextMenus(entity: EntityTreeBranchModel): void {
        const parent = this.getActiveParentEntity(entity);
        if (entity.pool != null) {
            entity.pool.forEach((item) => {
                item.contextMenuActions = [];
                this.updatePoolItemContextMenu(item, entity, parent);
            });
            if (this.enableEntity(entity) && entity.updatePoolItemsAfterRemoveElementFromConnections != null) {
                entity.pool = [...entity.updatePoolItemsAfterRemoveElementFromConnections(entity.pool, entity.connections)];
            }
        }
    }

    /**
     * Updates the data related to the entity added to the path.
     * @param entity the related entity.
     * @param branch the current branch where the updates are applied.
     */
    private updateEntityAttributesRecursively = (entity: EntityTreeBranchModel,
                                                 branch: Map<EntityTypeEnum, EntityTreeBranchModel>): void => {
        if (entity != null && branch != null) {
            // The relationship tree node
            const node = this.entityUtil.getCurrentEntityTreeNode(
                    entity, this.configurationTreeNode, this.entityTree, this.entitiesData);

            this.resetEntityAttributesRecursively(entity);
            this.applyChangesRecursively(entity, node);
        }
    };

    /**
     * Applies changes recursively regarding the path, connections and pool connectedWithParent property.
     * It use the entity.pathTo to reset also the entity tree branches related.
     *
     * @param entity the entity where changes will be applied.
     * @param configurationNode the configuration node representing the related configurations.
     */
    private applyChangesRecursively(entity: EntityTreeBranchModel, configurationNode: TreeNodeModel): void {

        if (entity != null) {
            const entityData = this.getEntityDisplayData(entity);
            const connections: EntityElementModel[] = [];
            if (entity.pool != null) {
                entity.pool.forEach((item) => {
                    let match = null;
                    if (configurationNode != null && configurationNode.relationTree != null) {
                        match = this.retrieveEntryFromArrayByCode(configurationNode.relationTree, item.code);
                    }
                    if (match != null) {
                        connections.push({
                            ...item,
                            connectedWithParent: this.connectionItemIsConnectedWithParent(item),
                            action: EntityElementActionsEnum.DELETE,
                            parentCode: configurationNode.code,
                            contextMenuActions: [],
                            pathState: this.entityPathState.getElementPathState(entity, item,
                                    this.configurationTreeNode, this.entityTree, this.entitiesData)
                        });
                    }
                });
            }
            if (entityData.hasConnections) {
                // Set the connections values
                entity.connections = connections;
                // Set path value
                if (entityData.hasPath) {
                    // in case of having only 1 element in the connections
                    if (connections.length === 1) {
                        connections[0].isSelected = true;
                        this.addToPath(connections[0], entity);
                    } else {
                        entity.path = null;
                    }
                }
            }
            // Update entity pool context menus
            this.updateCurrentEntityPoolItemsContextMenus(entity);
        }
    }

    /**
     * Reset connections and path of a given entity and for each element of the entity pool set the connectedWithParent to false.
     * It use the entity.pathTo to reset also the entity tree branches related.
     * @param entity the node entity to be reset.
     */
    private resetEntityAttributesRecursively(entity: EntityTreeBranchModel): void {
        if (entity != null) {
            entity.connections = [];
            entity.path = null;
            this.updateCurrentEntityPoolItemsContextMenus(entity);
            if (entity.pathTo != null) {
                entity.pathTo.forEach((child) => {
                    const entityBranch = this.entityUtil.getEntityBranch(child.type, this.entityTree);
                    if (entityBranch != null) {
                        this.resetEntityAttributesRecursively(entityBranch.get(child.type));
                    }
                });
            }
        }
    }

    private connectionItemIsConnectedWithParent(item: EntityElementModel): boolean {
        return item.displayParentCode != null;
    }

    /**
     * Returns the type ({@link EntityTreeBranchTypeEnum}) of the selected specific branch tab
     */
    private getSelectedSpecificBranchType(): EntityTreeBranchTypeEnum {
        let typeEnum: EntityTreeBranchTypeEnum = null;
        const branchOptionModel = this.entityTree.specificBranchOptions.find((sb) => sb.isSelected);
        if (branchOptionModel != null) {
            typeEnum = branchOptionModel.value;
        }
        return typeEnum;
    }

    /**
     * Returns the selected specific branch
     */
    private getSelectedSpecificBranch(): Map<EntityTypeEnum, EntityTreeBranchModel> {
        let branch: Map<EntityTypeEnum, EntityTreeBranchModel> = null;
        if (this.entityTree.specificBranches != null) {
            branch = this.entityTree.specificBranches.get(this.getSelectedSpecificBranchType());
        }
        return branch;
    }
}
